import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { Check, Pipette } from 'lucide-react';

const COLOR_PALETTE = [
  // Reds
  '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#991b1b',
  // Oranges
  '#fdba74', '#fb923c', '#f97316', '#ea580c', '#9a3412',
  // Yellows  
  '#fde047', '#facc15', '#eab308', '#ca8a04', '#854d0e',
  // Greens
  '#86efac', '#4ade80', '#22c55e', '#16a34a', '#166534',
  // Teals
  '#5eead4', '#2dd4bf', '#14b8a6', '#0d9488', '#115e59',
  // Blues
  '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1e40af',
  // Indigos
  '#a5b4fc', '#818cf8', '#6366f1', '#4f46e5', '#3730a3',
  // Purples
  '#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#5b21b6',
  // Pinks
  '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#9d174d',
  // Grays
  '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#1f2937',
];

export function ColorPicker({ value, onChange, className }) {
  const [hex, setHex] = useState(value || '#6b7280');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setHex(value || '#6b7280');
  }, [value]);

  const handleHexChange = (e) => {
    const v = e.target.value;
    setHex(v);
    if (/^#[0-9A-Fa-f]{6}$/.test(v)) {
      onChange(v);
    }
  };

  const selectColor = (c) => {
    setHex(c);
    onChange(c);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "w-7 h-7 rounded-full border-2 border-border hover:border-foreground/50 transition-all hover:scale-110 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
            className
          )}
          style={{ backgroundColor: value || '#6b7280' }}
          data-testid="color-picker-trigger"
        />
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3" align="start" side="bottom" sideOffset={4}>
        <div className="space-y-3">
          <div className="grid grid-cols-10 gap-1" data-testid="color-palette-grid">
            {COLOR_PALETTE.map((c) => (
              <button
                key={c}
                onClick={() => selectColor(c)}
                className={cn(
                  "w-6 h-6 rounded-md border transition-all hover:scale-125 hover:z-10 relative",
                  value === c
                    ? "border-foreground ring-1 ring-foreground scale-110"
                    : "border-transparent hover:border-foreground/30"
                )}
                style={{ backgroundColor: c }}
                data-testid={`color-${c}`}
              >
                {value === c && (
                  <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1 border-t">
            <Pipette className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-2 flex-1">
              <div
                className="w-6 h-6 rounded border flex-shrink-0"
                style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : '#6b7280' }}
              />
              <Input
                value={hex}
                onChange={handleHexChange}
                placeholder="#000000"
                className="h-7 text-xs font-mono"
                maxLength={7}
                data-testid="color-hex-input"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
